import React, { useState, useEffect } from "react";
import { calculateCombinedTax, AgeGroup } from "../utils/taxCalculator";
import * as XLSX from "xlsx";
import { useAuth } from "./AuthProvider";
import { getUser, patchUser, createCalculation, updateCalculation, isApiConfigured } from "../utils/api";
import CalculatorSeoSection from "./CalculatorSeoSection";
import AdSenseSlot from "./AdSenseSlot";
import SiteFooter from "./SiteFooter";
import "./TaxCalculator.css";

export type SavedTax = {
  incomeStreams?: string[];
  incomeStreamNames?: string[];
  ageGroup?: AgeGroup;
  incomeType?: "monthly" | "annual";
};

interface TaxCalculatorProps {
  onOpenAuthForSave?: (callback: () => void) => void;
  onOpenAuth?: () => void;
  pendingRestore?: Record<string, unknown>;
  pendingCalculationId?: string;
  pendingName?: string;
  pendingCreatedAt?: string;
  onClearPendingRestore?: () => void;
}

const TaxCalculator: React.FC<TaxCalculatorProps> = ({ onOpenAuthForSave, onOpenAuth, pendingRestore, pendingCalculationId, pendingName, pendingCreatedAt, onClearPendingRestore }) => {
  const { user } = useAuth();
  const [incomeStreams, setIncomeStreams] = useState<string[]>(["", ""]);
  const [incomeStreamNames, setIncomeStreamNames] = useState<string[]>([
    "",
    "",
  ]);
  const [ageGroup, setAgeGroup] = useState<AgeGroup>("under65");
  const [incomeType, setIncomeType] = useState<"monthly" | "annual">("monthly");
  const [showIncomeInfo, setShowIncomeInfo] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [loadedCalculationId, setLoadedCalculationId] = useState<string | null>(null);
  const [loadedName, setLoadedName] = useState<string | null>(null);
  const [loadedCreatedAt, setLoadedCreatedAt] = useState<string | null>(null);

  const canSaveAsUser = Boolean(user && isApiConfigured());

  const formatLoadedDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });

  const handleSaveClick = (forceNew = false) => {
    if (canSaveAsUser) {
      handleSave(forceNew);
    } else if (!user) {
      if (onOpenAuthForSave) {
        onOpenAuthForSave(() => handleSave(forceNew));
      } else {
        onOpenAuth?.();
      }
    }
  };

  useEffect(() => {
    if (!user || !isApiConfigured() || loadedOnce) return;
    setLoadedOnce(true);
    getUser()
      .then((data) => {
        const s = data.tax as SavedTax | undefined;
        if (!s) return;
        if (s.incomeStreams?.length) {
          setIncomeStreams(s.incomeStreams);
          const names = s.incomeStreamNames ?? [];
          const padded = s.incomeStreams.map((_, i) => names[i] ?? "");
          setIncomeStreamNames(padded);
        }
        if (s.ageGroup) setAgeGroup(s.ageGroup);
        if (s.incomeType) setIncomeType(s.incomeType);
      })
      .catch(() => setLoadedOnce(true));
  }, [user, loadedOnce]);

  useEffect(() => {
    if (!pendingRestore || !onClearPendingRestore) return;
    setLoadedOnce(true);
    if (pendingCalculationId) setLoadedCalculationId(pendingCalculationId);
    if (pendingName) setLoadedName(pendingName);
    if (pendingCreatedAt) setLoadedCreatedAt(pendingCreatedAt);
    const s = pendingRestore as SavedTax;
    if (s.incomeStreams?.length) {
      setIncomeStreams(s.incomeStreams.map(String));
      const names = s.incomeStreamNames ?? [];
      setIncomeStreamNames(s.incomeStreams.map((_, i) => (names[i] != null ? String(names[i]) : "")));
    }
    if (s.ageGroup) setAgeGroup(s.ageGroup);
    if (s.incomeType) setIncomeType(s.incomeType);
    onClearPendingRestore();
  }, [pendingRestore, pendingCalculationId, onClearPendingRestore]);

  const handleSave = async (forceNew = false) => {
    if (!canSaveAsUser) return;
    setSaveStatus("saving");
    const payload = {
      incomeStreams,
      incomeStreamNames,
      ageGroup,
      incomeType,
    };
    try {
      if (loadedCalculationId && !forceNew) {
        await updateCalculation(loadedCalculationId, { payload });
      } else {
        const created = await createCalculation({
          calculationType: "tax",
          payload,
        });
        setLoadedCalculationId(created.calculationId);
        setLoadedName(created.name ?? null);
        setLoadedCreatedAt(created.createdAt ?? null);
      }
      await patchUser({ tax: payload }).catch(() => {});
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2000);
    }
  };

  const handleIncomeChange = (index: number, value: string) => {
    const newStreams = [...incomeStreams];
    newStreams[index] = value;
    setIncomeStreams(newStreams);
  };

  const addIncomeStream = () => {
    setIncomeStreams([...incomeStreams, ""]);
    setIncomeStreamNames([...incomeStreamNames, ""]);
  };

  const removeIncomeStream = (index: number) => {
    if (incomeStreams.length > 1) {
      const newStreams = incomeStreams.filter((_, i) => i !== index);
      const newNames = incomeStreamNames.filter((_, i) => i !== index);
      setIncomeStreams(newStreams);
      setIncomeStreamNames(newNames);
    }
  };

  const handleNameChange = (index: number, value: string) => {
    const newNames = [...incomeStreamNames];
    newNames[index] = value;
    setIncomeStreamNames(newNames);
  };

  const calculateResults = () => {
    const validIncomes = incomeStreams
      .map((stream) => parseFloat(stream.replace(/[,\s]/g, "")) || 0)
      .filter((income) => income > 0);

    if (validIncomes.length === 0) return null;

    // Convert to annual income for tax calculation (SARS uses annual brackets)
    const annualIncomes =
      incomeType === "monthly"
        ? validIncomes.map((income) => income * 12)
        : validIncomes;

    const calculation = calculateCombinedTax(annualIncomes, ageGroup);
    return {
      ...calculation,
      validIncomes, // Include the valid incomes for display (in original format)
      annualIncomes, // Store annual incomes for reference
    };
  };

  const results = calculateResults();
  const hasResult = results !== null;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getIncomeStreamName = (index: number): string => {
    const name = incomeStreamNames[index]?.trim();
    return name || `Income Stream ${index + 1}`;
  };

  const exportToExcel = () => {
    if (!results) return;

    // Prepare data for export
    const worksheetData: any[] = [];

    // Add header
    worksheetData.push(["SARS Income Tax Calculator - Results"]);
    worksheetData.push([
      "Tax Year: 2025/2026 (1 March 2025 - 28 February 2026)",
    ]);
    worksheetData.push([`Age Group: ${ageGroup}`]);
    worksheetData.push([
      `Income Type: ${incomeType === "monthly" ? "Monthly" : "Annual"}`,
    ]);
    worksheetData.push([]);

    // Individual Results
    worksheetData.push(["Individual Income Streams"]);
    worksheetData.push([
      "Stream",
      "Name",
      `${incomeType === "monthly" ? "Monthly" : "Annual"} Income`,
      "Annual Income",
      "Tax Before Rebate",
      "Rebate Applied",
      "Tax Payable",
      ...(incomeType === "monthly" ? ["Monthly Tax"] : []),
      "Effective Rate",
      "Net Income (Annual)",
      ...(incomeType === "monthly" ? ["Net Income (Monthly)"] : []),
    ]);

    results.individualResults.forEach((result, index) => {
      const income = results.validIncomes[index];
      const annualIncome = incomeType === "monthly" ? income * 12 : income;
      const taxRebate = result.taxBeforeRebate - result.taxAfterRebate;

      const row: any[] = [
        index + 1,
        getIncomeStreamName(index),
        incomeType === "monthly"
          ? formatCurrency(income)
          : formatCurrency(annualIncome),
        formatCurrency(annualIncome),
        formatCurrency(result.taxBeforeRebate),
        `-${formatCurrency(taxRebate)}`,
        formatCurrency(result.taxAfterRebate),
        ...(incomeType === "monthly"
          ? [formatCurrency(result.taxAfterRebate / 12)]
          : []),
        `${result.effectiveRate.toFixed(2)}%`,
        formatCurrency(annualIncome - result.taxAfterRebate),
        ...(incomeType === "monthly"
          ? [formatCurrency((annualIncome - result.taxAfterRebate) / 12)]
          : []),
      ];
      worksheetData.push(row);
    });

    worksheetData.push([]);

    // Combined Results
    worksheetData.push(["Combined Tax Analysis"]);
    worksheetData.push([
      "Total Individual Taxes",
      formatCurrency(results.combinedResult.individualTaxTotal),
    ]);
    worksheetData.push([
      "Tax on Combined Income",
      formatCurrency(results.combinedResult.combinedTax),
    ]);
    worksheetData.push([
      "Additional Tax Due",
      formatCurrency(results.combinedResult.additionalTax),
    ]);
    worksheetData.push([]);

    // Total Summary
    worksheetData.push(["Total Summary"]);
    worksheetData.push([
      "Total Gross Income (Annual)",
      formatCurrency(results.combinedResult.totalIncome),
    ]);
    if (incomeType === "monthly") {
      worksheetData.push([
        "Total Gross Income (Monthly)",
        formatCurrency(results.combinedResult.totalIncome / 12),
      ]);
    }
    worksheetData.push([
      "Total Tax Payable",
      formatCurrency(results.combinedResult.combinedTax),
    ]);
    if (incomeType === "monthly") {
      worksheetData.push([
        "Total Tax (Monthly)",
        formatCurrency(results.combinedResult.combinedTax / 12),
      ]);
    }
    worksheetData.push([
      "Total Net Income (Annual)",
      formatCurrency(
        results.combinedResult.totalIncome - results.combinedResult.combinedTax
      ),
    ]);
    if (incomeType === "monthly") {
      worksheetData.push([
        "Total Net Income (Monthly)",
        formatCurrency(
          (results.combinedResult.totalIncome -
            results.combinedResult.combinedTax) /
            12
        ),
      ]);
    }

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();

    // Set column widths for better formatting
    // Find max column width across all rows
    let maxCol = 0;
    worksheetData.forEach((row) => {
      if (row.length > maxCol) maxCol = row.length - 1;
    });

    const colWidths = [];
    for (let i = 0; i <= maxCol; i++) {
      // First column (stream number) narrower, name and values wider
      const width = i === 0 ? 8 : i === 1 ? 30 : 20;
      colWidths.push({ wch: width });
    }
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Tax Calculation");

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `tax-calculation-${timestamp}.xlsx`;

    // Export file - xlsx format for best Excel and Numbers compatibility
    XLSX.writeFile(wb, filename);
  };

  return (
    <main className="tax-calculator">
      <div className="calculator-container">
        <div className="calculator-title-row">
          <div>
            <h1>SARS Income Tax Calculator</h1>
            {loadedCalculationId && loadedName ? (
              <p className="loaded-file-label">
                {loadedName}{loadedCreatedAt ? ` – ${formatLoadedDate(loadedCreatedAt)}` : ""}
              </p>
            ) : (
              <p className="subtitle">
                Calculate tax for one or multiple income streams — no accountant uncle
                required.
              </p>
            )}
            <p className="tax-year-info">
              Tax Year: 2025/2026 (1 March 2025 - 28 February 2026)
            </p>
          </div>
        </div>

        <div className="calculator-section">
          <h2>Age Group</h2>
          <div className="age-selector">
            <label>
              <input
                type="radio"
                value="under65"
                checked={ageGroup === "under65"}
                onChange={(e) => setAgeGroup(e.target.value as AgeGroup)}
              />
              Under 65
            </label>
            <label>
              <input
                type="radio"
                value="65to74"
                checked={ageGroup === "65to74"}
                onChange={(e) => setAgeGroup(e.target.value as AgeGroup)}
              />
              65 - 74 years
            </label>
            <label>
              <input
                type="radio"
                value="75plus"
                checked={ageGroup === "75plus"}
                onChange={(e) => setAgeGroup(e.target.value as AgeGroup)}
              />
              75 years and older
            </label>
          </div>
        </div>

        <div className="calculator-section">
          <h2>Income Type</h2>
          <div className="age-selector">
            <label>
              <input
                type="radio"
                value="monthly"
                checked={incomeType === "monthly"}
                onChange={(e) =>
                  setIncomeType(e.target.value as "monthly" | "annual")
                }
              />
              Monthly
            </label>
            <label>
              <input
                type="radio"
                value="annual"
                checked={incomeType === "annual"}
                onChange={(e) =>
                  setIncomeType(e.target.value as "monthly" | "annual")
                }
              />
              Annual
            </label>
          </div>
        </div>

        <div className="calculator-section">
          <div className="income-header">
            <div className="heading-with-info">
              <h2>Income Streams</h2>
              <div className="info-icon-container">
                <button
                  className="info-icon"
                  onClick={() => setShowIncomeInfo(!showIncomeInfo)}
                  aria-label="What qualifies as taxable income?"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="currentColor"
                      fillOpacity="0.1"
                    />
                    <circle cx="12" cy="7" r="1.5" fill="currentColor" />
                    <rect
                      x="10"
                      y="11"
                      width="4"
                      height="8"
                      rx="0.5"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                {showIncomeInfo && (
                  <div className="info-tooltip">
                    <div className="info-tooltip-content">
                      <h4>What Qualifies as Taxable Income?</h4>
                      <p>
                        According to SARS, taxable income includes the usual
                        suspects — if it puts pap on the table, it probably
                        counts:
                      </p>
                      <ul>
                        <li>
                          <strong>Employment income:</strong> Salaries, wages,
                          bonuses, overtime pay, allowances
                        </li>
                        <li>
                          <strong>Business income:</strong> Profits from a
                          business or trade
                        </li>
                        <li>
                          <strong>Director's fees</strong>
                        </li>
                        <li>
                          <strong>Investment income:</strong> Interest and
                          foreign dividends
                        </li>
                        <li>
                          <strong>Rental income:</strong> Profits from rental
                          property
                        </li>
                        <li>
                          <strong>Trust income:</strong> Income as a beneficiary
                        </li>
                        <li>
                          <strong>Royalties, annuities, pension income</strong>
                        </li>
                        <li>
                          <strong>Certain capital gains</strong>
                        </li>
                      </ul>
                      <button
                        className="close-info"
                        onClick={() => setShowIncomeInfo(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button className="add-button" onClick={addIncomeStream}>
              + Add Income Stream
            </button>
          </div>

          {incomeStreams.map((stream, index) => (
            <div key={index} className="income-input-group">
              <div className="stream-name-input">
                <input
                  type="text"
                  value={incomeStreamNames[index]}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  placeholder={`Name for Income Stream ${index + 1} (optional)`}
                  className="name-input"
                />
              </div>
              <label>
                {incomeStreamNames[index]?.trim() ||
                  `Income Stream ${index + 1}`}{" "}
                ({incomeType === "monthly" ? "Monthly" : "Annual"})
              </label>
              <div className="input-wrapper">
                <span className="currency-symbol">R</span>
                <input
                  type="text"
                  value={stream}
                  onChange={(e) => handleIncomeChange(index, e.target.value)}
                  placeholder={`Enter ${incomeType} amount`}
                />
                {incomeStreams.length > 1 && (
                  <button
                    className="remove-button"
                    onClick={() => removeIncomeStream(index)}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {results && (
          <div className="results-section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0 }}>Tax Calculation Results</h2>
              <button className="export-button" onClick={exportToExcel}>
                📊 Export to Excel
              </button>
            </div>

            <div className="individual-results">
              <h3>Individual Tax per Stream</h3>
              {results.individualResults.map((result, index) => {
                const income = results.validIncomes[index];
                const annualIncome =
                  incomeType === "monthly" ? income * 12 : income;

                return (
                  <div key={index} className="result-card">
                    <div className="result-header">
                      <span className="result-label">
                        {getIncomeStreamName(index)}
                      </span>
                      <span className="result-value">
                        {formatCurrency(annualIncome)}{" "}
                        {incomeType === "monthly" && (
                          <span
                            style={{ fontSize: "0.7em", fontWeight: "normal" }}
                          >
                            ({formatCurrency(income)}/month)
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="result-details">
                      <div className="result-row">
                        <span>Tax Before Rebate:</span>
                        <span>{formatCurrency(result.taxBeforeRebate)}</span>
                      </div>
                      <div className="result-row">
                        <span>Rebate Applied:</span>
                        <span className="success">
                          -
                          {formatCurrency(
                            result.taxBeforeRebate - result.taxAfterRebate
                          )}
                        </span>
                      </div>
                      <div className="result-row">
                        <span>Tax Payable (Annual):</span>
                        <span className="highlight">
                          {formatCurrency(result.taxAfterRebate)}
                        </span>
                      </div>
                      {incomeType === "monthly" && (
                        <div
                          className="result-row"
                          style={{ color: "#718096" }}
                        >
                          <span>Monthly Tax:</span>
                          <span>
                            {formatCurrency(result.taxAfterRebate / 12)}
                          </span>
                        </div>
                      )}
                      <div className="result-row">
                        <span>Effective Rate:</span>
                        <span>{result.effectiveRate.toFixed(2)}%</span>
                      </div>
                      <div className="result-row total-row">
                        <span>Net Income (Annual):</span>
                        <span
                          className="highlight"
                          style={{ color: "#48bb78" }}
                        >
                          {formatCurrency(annualIncome - result.taxAfterRebate)}
                        </span>
                      </div>
                      {incomeType === "monthly" && (
                        <div
                          className="result-row"
                          style={{ color: "#718096" }}
                        >
                          <span>Net Income (Monthly):</span>
                          <span>
                            {formatCurrency(
                              (annualIncome - result.taxAfterRebate) / 12
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="combined-results">
              <h3>Combined Tax Analysis</h3>
              <div className="result-card combined-card">
                <div className="result-row">
                  <span>Total of Individual Taxes:</span>
                  <span>
                    {formatCurrency(results.combinedResult.individualTaxTotal)}
                  </span>
                </div>
                <div className="result-row">
                  <span>Tax on Combined Income:</span>
                  <span className="highlight">
                    {formatCurrency(results.combinedResult.combinedTax)}
                  </span>
                </div>
                <div className="result-row total-row">
                  <span>Additional Tax Due:</span>
                  <span
                    className={
                      results.combinedResult.additionalTax > 0
                        ? "warning"
                        : "success"
                    }
                  >
                    {formatCurrency(results.combinedResult.additionalTax)}
                  </span>
                </div>
              </div>

              <div
                className="result-card"
                style={{ marginTop: "20px", borderColor: "#48bb78" }}
              >
                <h3 style={{ marginTop: 0, marginBottom: "15px" }}>
                  Total Summary
                </h3>
                <div className="result-row">
                  <span>Total Gross Income:</span>
                  <span className="highlight">
                    {formatCurrency(results.combinedResult.totalIncome)}
                  </span>
                </div>
                {incomeType === "monthly" && (
                  <div className="result-row" style={{ color: "#718096" }}>
                    <span>Total Gross Income (Monthly):</span>
                    <span>
                      {formatCurrency(results.combinedResult.totalIncome / 12)}
                    </span>
                  </div>
                )}
                <div className="result-row">
                  <span>Total Tax Payable:</span>
                  <span className="highlight">
                    {formatCurrency(results.combinedResult.combinedTax)}
                  </span>
                </div>
                {incomeType === "monthly" && (
                  <div className="result-row" style={{ color: "#718096" }}>
                    <span>Total Tax (Monthly):</span>
                    <span>
                      {formatCurrency(results.combinedResult.combinedTax / 12)}
                    </span>
                  </div>
                )}
                <div
                  className="result-row total-row"
                  style={{ borderTopColor: "#48bb78" }}
                >
                  <span>Total Net Income:</span>
                  <span
                    className="highlight"
                    style={{
                      color: "#48bb78",
                      fontSize: "1.2em",
                      fontWeight: "bold",
                    }}
                  >
                    {formatCurrency(
                      results.combinedResult.totalIncome -
                        results.combinedResult.combinedTax
                    )}
                  </span>
                </div>
                {incomeType === "monthly" && (
                  <div
                    className="result-row"
                    style={{ color: "#48bb78", fontWeight: "600" }}
                  >
                    <span>Total Net Income (Monthly):</span>
                    <span>
                      {formatCurrency(
                        (results.combinedResult.totalIncome -
                          results.combinedResult.combinedTax) /
                          12
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {results.combinedResult.additionalTax > 0 && (
              <div className="warning-box">
                <p>
                  ⚠️ Eish! Because your combined income places you in a higher
                  tax bracket, you will owe additional tax of{" "}
                  <strong>
                    {formatCurrency(results.combinedResult.additionalTax)}
                  </strong>
                  .
                </p>
              </div>
            )}
          </div>
        )}

        <div className="calculator-save-row">
          {loadedCalculationId ? (
            <>
              <button
                type="button"
                className="save-button"
                onClick={() => handleSaveClick(false)}
                disabled={!hasResult || saveStatus === "saving"}
              >
                {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Try again" : "Save"}
              </button>
              <button
                type="button"
                className="save-new-button"
                onClick={() => handleSaveClick(true)}
                disabled={!hasResult || saveStatus === "saving"}
              >
                Save new
              </button>
            </>
          ) : (
            <button
              type="button"
              className="save-button"
              onClick={() => handleSaveClick(false)}
              disabled={!hasResult || saveStatus === "saving"}
            >
              {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Try again" : "Save for later"}
            </button>
          )}
        </div>

        <AdSenseSlot className="AdSenseSlot-wrapper--in-panel" />
        <CalculatorSeoSection variant="tax" />
        <SiteFooter />
      </div>
    </main>
  );
};

export default TaxCalculator;
